import { TaxStrategy, roundTo2Decimals } from '@principle-theorem/accounting';
import {
  ITranslationMap,
  type IGetRecordResponse,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Invoice,
  Transaction,
  TransactionOperators,
  getTransactionAmount,
  stafferToNamedDoc,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  IServiceCodeLineItem,
  IStaffer,
  ITreatmentBasePriceLineItem,
  ITreatmentConfiguration,
  ITreatmentLineItem,
  ITreatmentRef,
  InvoiceLineItemType,
  InvoiceStatus,
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IBasePatient,
  type ICustomLineItem,
  type IFeeLineItem,
  type IInvoice,
  type IPatient,
  type IPatientContactDetails,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithPrimaryKey,
  INamedDocument,
  resolveSequentially,
  sortByCreatedAt,
  sortTimestampAsc,
  toISODate,
  toNamedDocument,
  type IIdentifiable,
  toMomentTz,
  Firestore,
  toTimestamp,
} from '@principle-theorem/shared';
import { compact, first, flatten, groupBy, remove, sumBy } from 'lodash';
import { resolveMappedCodeTreatmentPair } from '../../../../mappings/item-codes';
import { ItemCodeResourceMapType } from '../../../../mappings/item-codes-to-xlsx';
import { TranslationMapHandler } from '../../../../translation-map';
import {
  ExactTransaction,
  ExactTransactionType,
  type IExactTransaction,
} from '../../../source/entities/patient-transactions';
import { ExactTreatment } from '../../../source/entities/patient-treatments';
import { exactToothConverter } from '../../../util/tooth';
import {
  buildExactRefundAdjustmentIndex,
  IExactRefundAdjustmentIndex,
} from '../../../lib/exact-refund-adjustments';
import { resolveExactStaffLocation } from '../../mappings/practitioner-to-practice-mapping';
import { resolveExactStaffer } from '../../mappings/staff';
import { PATIENT_RESOURCE_TYPE } from '../../../../destination/entities/patient';

const EPSILON = 0.005;

function roughlyEqual(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON;
}

interface ITransactionByPlanMap {
  [planId: string]: {
    transactions: IGetRecordResponse<ExactTransaction>[];
    treatments: IGetRecordResponse<ExactTreatment>[];
    transactionAmount: number;
    treatmentsAmount: number;
  };
}

interface IPatientAccountSummary {
  nonTreatmentTransactions: IGetRecordResponse<ExactTransaction>[];
  nonTreatmentCharges: IGetRecordResponse<ExactTransaction>[];
  treatmentTransactions: ITransactionByPlanMap;
  totalCharged: number;
  totalPaid: number;
  totalOwing: number;
  allPayments: IGetRecordResponse<ExactTransaction>[];
  allTransactions: (IIdentifiable & ITransaction)[];
  allCharges: IGetRecordResponse<ExactTransaction>[];
}

export function isExactPayment(transaction: IExactTransaction): boolean {
  if (!transaction.type) {
    return false;
  }

  // If its a transferFrom and the amount is positive, they are moving debt
  // so we consider it a charge
  if (
    transaction.type === ExactTransactionType.TransferFrom &&
    transaction.amount > 0
  ) {
    return false;
  }

  // If its a transferTo and the amount is positive, they are receiving debt
  // so we consider it a charge
  if (
    transaction.type === ExactTransactionType.TransferTo &&
    transaction.amount > 0
  ) {
    return false;
  }

  return [
    ExactTransactionType.Payment,
    ExactTransactionType.Adjustment,
    ExactTransactionType.WriteOff,
    ExactTransactionType.TransferFrom,
    ExactTransactionType.TransferTo,
  ].includes(transaction.type);
}

export function determineTransactionType(
  transaction: IExactTransaction
): TransactionType {
  return transaction.amount < 0
    ? TransactionType.Incoming
    : TransactionType.Outgoing;
}

interface IInvoiceBuildData {
  invoice: IIdentifiable & IInvoice;
  transactions: (IIdentifiable & ITransaction)[];
}

export class ExactInvoiceBuilder {
  accountSummary: IPatientAccountSummary = {
    nonTreatmentTransactions: [],
    nonTreatmentCharges: [],
    treatmentTransactions: {},
    totalCharged: 0,
    totalPaid: 0,
    totalOwing: 0,
    allPayments: [],
    allTransactions: [],
    allCharges: [],
  };
  refundAdjustmentIndex: IExactRefundAdjustmentIndex | undefined;

  constructor(
    public sourceTransactions: IGetRecordResponse<ExactTransaction>[],
    public patientTreatments: IGetRecordResponse<ExactTreatment>[],
    public patientBalance: number,
    public staff: WithPrimaryKey<ITranslationMap<IStaffer, unknown>>[],
    public sourceItemCodes: WithPrimaryKey<
      ITranslationMap<object, ItemCodeResourceMapType>
    >[],
    public sourceTreatmentMappings: WithPrimaryKey<
      ITranslationMap<ITreatmentConfiguration>
    >[],
    public staffToPractice: WithPrimaryKey<ITranslationMap<IPractice>>[]
  ) {}

  reconcileTransactions(
    sourceTransactions: IGetRecordResponse<ExactTransaction>[],
    patientBalance: number
  ): IGetRecordResponse<ExactTransaction>[] {
    const combinedTotal = roundTo2Decimals(
      sourceTransactions.reduce(
        (total, transaction) => total + transaction.data.amount,
        0
      )
    );

    const discrepancy = roundTo2Decimals(combinedTotal - patientBalance);
    if (!roughlyEqual(combinedTotal, patientBalance) && discrepancy > 0) {
      let remainingToRemove = discrepancy;

      const reconciledTransactions = this.sourceTransactions.map(
        (transaction) => {
          if (
            // transaction.data.type === ExactTransactionType.Invoice &&
            !!transaction.data.openAmount &&
            transaction.data.openAmount > 0 &&
            remainingToRemove > EPSILON
          ) {
            const toRemove = Math.min(
              transaction.data.openAmount,
              remainingToRemove
            );
            transaction.data.amount -= toRemove;
            remainingToRemove = roundTo2Decimals(remainingToRemove - toRemove);
          }
          return transaction;
        }
      );

      if (remainingToRemove > EPSILON) {
        throw new Error(
          `Patient balance discrepancy of ${discrepancy} could not be resolved. Remaining to remove: ${remainingToRemove}`
        );
      }

      return reconciledTransactions;
    }
    return sourceTransactions;
  }

  async buildAccountSummary(
    migrationPractices: INamedDocument<IPractice>[],
    translationMap: TranslationMapHandler
  ): Promise<void> {
    this.sourceTransactions = this.reconcileTransactions(
      this.sourceTransactions,
      this.patientBalance
    );
    this.refundAdjustmentIndex = buildExactRefundAdjustmentIndex(
      this.sourceTransactions
    );

    const nonTreatmentTransactions = this.sourceTransactions.filter(
      (transaction) => !transaction.data.treatmentId
    );

    const nonTreatmentCharges = nonTreatmentTransactions.filter(
      (transaction) => !isExactPayment(transaction.data)
    );

    const allPayments = this.sourceTransactions.filter((transaction) =>
      isExactPayment(transaction.data)
    );
    const allTransactions = await this.getTransactions(
      allPayments,
      migrationPractices,
      translationMap
    );
    const allCharges = this.sourceTransactions.filter(
      (transaction) => !isExactPayment(transaction.data)
    );

    const treatmentTransactions: ITransactionByPlanMap = {};

    this.sourceTransactions.map((transaction) => {
      const planId = transaction.data.treatmentId;
      if (!planId) {
        return;
      }
      const treatments = this.patientTreatments.filter(
        (patientTreatment) => patientTreatment.data.treatment_plan_id === planId
      );

      const treatmentsAmount = treatments.reduce(
        (total, treatment) => total + treatment.data.total_amount,
        0
      );
      const transactionAmount = transaction.data.amount;
      const existingPlan = treatmentTransactions[planId];

      if (existingPlan) {
        existingPlan.transactions = [
          ...(existingPlan.transactions ?? []),
          transaction,
        ];
        const amount = sumBy(
          existingPlan.transactions,
          (record) => record.data.amount
        );
        existingPlan.transactionAmount = amount;
      } else {
        treatmentTransactions[planId] = {
          transactions: [transaction],
          treatments,
          transactionAmount,
          treatmentsAmount,
        };
      }
    });

    const totalCharged = this.sourceTransactions
      .filter((transaction) => !isExactPayment(transaction.data))
      .reduce((total, transaction) => total + transaction.data.amount, 0);
    const totalPaid = this.sourceTransactions
      .filter((transaction) => isExactPayment(transaction.data))
      .reduce((total, transaction) => total + transaction.data.amount, 0);

    this.accountSummary = {
      totalCharged: roundTo2Decimals(totalCharged),
      totalPaid: roundTo2Decimals(totalPaid),
      totalOwing: roundTo2Decimals(this.patientBalance),
      nonTreatmentTransactions,
      nonTreatmentCharges,
      treatmentTransactions,
      allPayments,
      allCharges,
      allTransactions,
    };
  }

  async buildInvoices(
    patient: IBasePatient & IPatientContactDetails,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<IInvoiceBuildData[]> {
    const treatmentInvoices = await this.buildTreatmentInvoices(
      patient,
      translationMap,
      migrationPractices
    );
    const nonTreatmentInvoices = await this.buildNonTreatmentInvoices(
      patient,
      translationMap,
      migrationPractices
    );
    const invoiceBuildData = [
      ...nonTreatmentInvoices,
      ...treatmentInvoices,
    ].sort((nonTreatmentInvoice, treatmentInvoice) =>
      sortTimestampAsc(
        nonTreatmentInvoice.invoice.createdAt,
        treatmentInvoice.invoice.createdAt
      )
    );
    let transactions = this.accountSummary.allTransactions.sort(
      (transactionA, transactionB) =>
        sortTimestampAsc(transactionA.createdAt, transactionB.createdAt)
    );

    const results: IInvoiceBuildData[] = [];
    invoiceBuildData.map((invoiceData) => {
      if (invoiceData.transactions.length) {
        const balance = Invoice.balance(
          invoiceData.invoice,
          invoiceData.transactions
        );
        if (balance <= 0) {
          results.push(invoiceData);
          return;
        }
      }

      const [transactionsForInvoice, remainingTransactions] =
        getTransactionsForBalance(
          transactions,
          Invoice.balance(invoiceData.invoice, invoiceData.transactions)
        );
      transactions = remainingTransactions;

      results.push({
        invoice: invoiceData.invoice,
        transactions: [...invoiceData.transactions, ...transactionsForInvoice],
      });
    });

    if (transactions.length > 0) {
      const depositInvoices = await this.buildDepositInvoices(
        patient,
        transactions
      );
      depositInvoices.map((depositInvoiceData) => {
        const balance = Invoice.balance(
          depositInvoiceData.invoice,
          depositInvoiceData.transactions
        );
        if (balance !== 0) {
          throw new Error(
            `Deposit invoice balance is not zero: ${balance} for invoice: ${depositInvoiceData.invoice.uid}`
          );
        }
      });

      results.push(...depositInvoices);
    }

    return results.map((invoiceData) => {
      const status = this.getInvoiceStatus(
        Invoice.balance(invoiceData.invoice, invoiceData.transactions)
      );
      const lastTransaction = new TransactionOperators(invoiceData.transactions)
        .completed()
        .sort(sortByCreatedAt)
        .reverse()
        .last();

      if (status === InvoiceStatus.Paid && lastTransaction) {
        invoiceData.invoice.paidAt = lastTransaction.createdAt;
      }
      Invoice.updateStatus(
        invoiceData.invoice,
        status,
        invoiceData.invoice.createdAt
      );

      return invoiceData;
    });
  }

  getInvoiceStatus(amountRemaining: number): InvoiceStatus {
    if (amountRemaining <= 0) {
      return InvoiceStatus.Paid;
    }

    if (amountRemaining > 0) {
      return InvoiceStatus.Issued;
    }

    return InvoiceStatus.Draft;
  }

  async buildTreatmentInvoices(
    patient: IBasePatient & IPatientContactDetails,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<IInvoiceBuildData[]> {
    const treatmentInvoices = await resolveSequentially(
      Object.values(this.accountSummary.treatmentTransactions),
      (summary) => {
        const charges = summary.transactions.filter(
          (transaction) => !isExactPayment(transaction.data)
        );
        const groupedByDateCharges = groupBy(charges, (charge) =>
          toISODate(charge.record.translations.date)
        );

        return resolveSequentially(
          Object.values(groupedByDateCharges),
          async (groupedCharges) => {
            const sourceInvoice = groupedCharges[0];
            const practice = await resolveExactStaffLocation(
              sourceInvoice.data.user_code ?? '',
              translationMap,
              this.staffToPractice,
              migrationPractices
            );
            if (!practice) {
              throw new Error(
                `Failed to resolve practice for user: ${sourceInvoice.data.user_code}`
              );
            }

            const date = toMomentTz(
              sourceInvoice.record.translations.date,
              practice.settings.timezone
            );

            const invoice = Invoice.init({
              due: toTimestamp(date),
              practice: toNamedDocument(practice),
              from: toAccountDetails(practice),
              to: toAccountDetails(patient),
              reference: sourceInvoice.data.reference ?? '',
              issuedAt: toTimestamp(date),
              createdAt: toTimestamp(date),
            });

            const treatmentsCompletedOnDate = summary.treatments.filter(
              (treatment) =>
                treatment.data.completed_date === sourceInvoice.data.date
            );

            if (!treatmentsCompletedOnDate.length) {
              // eslint-disable-next-line no-console
              console.error(
                `No treatments found for sourceInvoice: ${sourceInvoice.data.treatmentId} and date: ${sourceInvoice.data.date}`
              );
            }

            const nonTreatmentLineItems = remove(
              this.accountSummary.nonTreatmentCharges,
              (transaction) => transaction.data.date === sourceInvoice.data.date
            );
            const nonTreatmentItems = this.buildNonTreatmentLineItems(
              nonTreatmentLineItems
            );

            const lineItems: (ITreatmentLineItem | IFeeLineItem)[] = [];
            const transactions: (IIdentifiable & ITransaction)[] = [];

            await resolveSequentially(groupedCharges, async (charge) => {
              const refundPairs =
                this.refundAdjustmentIndex?.pairsByInvoiceReference.get(
                  charge.data.reference ?? ''
                ) ?? [];
              for (const refundPair of refundPairs) {
                const invoiceRefundTransactionIndex =
                  this.accountSummary.allTransactions.findIndex(
                    (transaction) =>
                      (transaction.extendedData as IExactTransaction).id ===
                      refundPair.invoiceRefundSourceId
                  );
                let refundDescription = `Refund - ${charge.data.reference}`;
                if (invoiceRefundTransactionIndex !== -1) {
                  const invoiceRefundTransaction =
                    this.accountSummary.allTransactions.splice(
                      invoiceRefundTransactionIndex,
                      1
                    )[0];
                  refundDescription =
                    invoiceRefundTransaction.description ?? refundDescription;
                }

                lineItems.push({
                  uid: refundPair.invoiceRefundSourceId,
                  type: InvoiceLineItemType.Fee,
                  description: refundDescription,
                  amount: -refundPair.amount,
                  tax: 0,
                  taxStatus: TaxStrategy.GSTApplicable,
                  quantity: 1,
                });

                if (refundPair.receiptRefundSourceId) {
                  const receiptRefundTransactionIndex =
                    this.accountSummary.allTransactions.findIndex(
                      (transaction) =>
                        (transaction.extendedData as IExactTransaction).id ===
                        refundPair.receiptRefundSourceId
                    );
                  if (receiptRefundTransactionIndex !== -1) {
                    const receiptRefundTransaction =
                      this.accountSummary.allTransactions.splice(
                        receiptRefundTransactionIndex,
                        1
                      )[0];
                    transactions.push(receiptRefundTransaction);
                  }
                }

                if (refundPair.originalPaymentSourceId) {
                  const originalPaymentTransactionIndex =
                    this.accountSummary.allTransactions.findIndex(
                      (transaction) =>
                        (transaction.extendedData as IExactTransaction).id ===
                        refundPair.originalPaymentSourceId
                    );
                  if (originalPaymentTransactionIndex !== -1) {
                    const originalPaymentTransaction =
                      this.accountSummary.allTransactions.splice(
                        originalPaymentTransactionIndex,
                        1
                      )[0];
                    transactions.push(originalPaymentTransaction);
                  }
                }
              }

              const adjustmentsForCharge = remove(
                this.accountSummary.allTransactions,
                (transaction) =>
                  transaction.reference === charge.data.reference &&
                  transaction.provider === TransactionProvider.Discount
              );
              transactions.push(...adjustmentsForCharge);

              const staffer = await resolveExactStaffer(
                charge.data.user_code ?? '',
                translationMap,
                this.staff
              );
              if (!staffer) {
                throw new Error(
                  `Failed to resolve staffer: ${charge.data.user_code}`
                );
              }

              const treatmentRef = {
                treatmentUuid: charge.data.id,
                attributedTo: stafferToNamedDoc(staffer),
              };

              const filteredTreatments = treatmentsCompletedOnDate.filter(
                (treatment) =>
                  treatment.data.provider_code === charge.data.user_code
              );

              const [serviceCodeItems, treatmentItems] =
                await this.getServiceCodeItems(
                  filteredTreatments,
                  translationMap
                );

              lineItems.push({
                uid: charge.data.id,
                type: InvoiceLineItemType.Treatment,
                treatmentRef,
                items: [
                  this.getBasePriceItem(charge, treatmentRef),
                  ...serviceCodeItems,
                ],
                description: charge.data.description,
                amount: charge.data.amount,
                tax: 0,
                taxStatus: TaxStrategy.GSTApplicable,
                quantity: 1,
              });

              if (treatmentItems.length) {
                lineItems.push(...treatmentItems);
              }
            });

            const isOnBehalfOf = treatmentsCompletedOnDate.find(
              (treatment) => treatment.data.patient_id !== patient.referenceId
            );
            if (isOnBehalfOf) {
              const fromRef = await translationMap.getDestination<IPatient>(
                isOnBehalfOf.data.patient_id,
                PATIENT_RESOURCE_TYPE
              );

              if (fromRef) {
                const childPatient = await Firestore.getDoc(fromRef);
                const onBehalfOf = toAccountDetails(
                  childPatient as IBasePatient & IPatientContactDetails
                );
                invoice.to = {
                  ...invoice.to,
                  onBehalfOf,
                };
              }
            }

            const items = [...lineItems, ...nonTreatmentItems];
            // Pre-attached reference/date transactions stay on the treatment invoice.
            return {
              invoice: {
                ...invoice,
                items,
                uid: sourceInvoice.data.id,
              },
              transactions,
            };
          }
        );
      }
    );

    const result = flatten(treatmentInvoices);
    return result;
  }

  getBasePriceItem(
    charge: IGetRecordResponse<ExactTransaction>,
    treatmentRef: ITreatmentRef
  ): ITreatmentBasePriceLineItem {
    return {
      uid: charge.data.id,
      type: InvoiceLineItemType.TreatmentBasePrice,
      treatmentRef,
      description: 'Base Price',
      amount: charge.data.amount,
      tax: 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };
  }

  async getTreatmentLineItem(
    treatment: IGetRecordResponse<ExactTreatment>,
    translationMap: TranslationMapHandler,
    mappedTreatment?: ITranslationMap<ITreatmentConfiguration>
  ): Promise<ITreatmentLineItem | undefined> {
    if (!mappedTreatment) {
      return;
    }

    const resolvedStaffer = await resolveExactStaffer(
      treatment.data.provider_code ?? '',
      translationMap,
      this.staff
    );
    if (!resolvedStaffer) {
      throw new Error(
        `Failed to resolve staffer for treatment: ${treatment.data.treatment_id}`
      );
    }

    const treatmentRef = {
      treatmentUuid: treatment.data.treatment_id,
      attributedTo: stafferToNamedDoc(resolvedStaffer),
    };

    return {
      uid: treatment.data.treatment_id,
      type: InvoiceLineItemType.Treatment,
      treatmentRef,
      items: [
        {
          uid: treatment.data.treatment_id,
          type: InvoiceLineItemType.TreatmentBasePrice,
          treatmentRef,
          description: '',
          amount: 0,
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        },
      ],
      description: `${treatment.data.service_code} - ${treatment.data.service_description}`,
      amount: 0,
      tax: 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    };
  }

  async getServiceCodeItems(
    treatments: IGetRecordResponse<ExactTreatment>[],
    translationMap: TranslationMapHandler
  ): Promise<[IServiceCodeLineItem[], ITreatmentLineItem[]]> {
    const serviceCodeItems: IServiceCodeLineItem[] = [];
    const treatmentLineItems: ITreatmentLineItem[] = [];
    await resolveSequentially(treatments, async (treatment) => {
      const [code, mappedTreatment] = resolveMappedCodeTreatmentPair(
        this.sourceItemCodes,
        this.sourceTreatmentMappings,
        treatment.data.service_code,
        treatment.data.service_code
      );

      if (!code) {
        const lineItem = await this.getTreatmentLineItem(
          treatment,
          translationMap,
          mappedTreatment
        );
        if (lineItem) {
          treatmentLineItems.push(lineItem);
        }
        return;
      }

      return serviceCodeItems.push({
        uid: treatment.data.treatment_id,
        type: InvoiceLineItemType.ServiceCode,
        code: code.code.toString(),
        toothId: treatment.data.tooth
          ? exactToothConverter(treatment.data.tooth)
          : undefined,
        description: `${code.code.toString()} -  ${
          treatment.data.service_description
        }`,
        amount: 0,
        tax: 0,
        taxStatus: TaxStrategy.GSTApplicable,
        quantity: 1,
      });
    });

    return [serviceCodeItems, treatmentLineItems];
  }

  async buildNonTreatmentInvoices(
    patient: IBasePatient & IPatientContactDetails,
    translationMap: TranslationMapHandler,
    migrationPractices: INamedDocument<IPractice>[]
  ): Promise<IInvoiceBuildData[]> {
    const charges = this.accountSummary.nonTreatmentCharges;
    if (!charges.length) {
      return [];
    }

    const groupedByDateCharges = groupBy(charges, (charge) =>
      toISODate(charge.record.translations.date)
    );
    return resolveSequentially(
      Object.values(groupedByDateCharges),
      async (groupedCharges) => {
        const sourceInvoice = groupedCharges[0];
        const practice = await resolveExactStaffLocation(
          sourceInvoice.data.user_code ?? '',
          translationMap,
          this.staffToPractice,
          migrationPractices
        );
        if (!practice) {
          throw new Error(
            `Failed to resolve practice for user: ${sourceInvoice.data.user_code}`
          );
        }

        const date = toMomentTz(
          sourceInvoice.record.translations.date,
          practice.settings.timezone
        );

        const invoice = Invoice.init({
          due: toTimestamp(date),
          practice: toNamedDocument(practice),
          from: toAccountDetails(practice),
          to: toAccountDetails(patient),
          reference: sourceInvoice.data.reference ?? '',
          issuedAt: toTimestamp(date),
          createdAt: toTimestamp(date),
        });

        const transactions: (IIdentifiable & ITransaction)[] = [];
        groupedCharges.map((charge) => {
          const adjustmentsForCharge = remove(
            this.accountSummary.allTransactions,
            (transaction) =>
              transaction.reference === charge.data.reference &&
              transaction.provider === TransactionProvider.Discount
          );
          transactions.push(...adjustmentsForCharge);
        });

        const items = this.buildNonTreatmentLineItems(groupedCharges);
        return {
          invoice: {
            ...invoice,
            items: items,
            uid: sourceInvoice.data.id,
          },
          transactions,
        };
      }
    );
  }

  async buildDepositInvoices(
    patient: IBasePatient & IPatientContactDetails,
    transactions: (IIdentifiable & ITransaction)[]
  ): Promise<IInvoiceBuildData[]> {
    if (!transactions) {
      return [];
    }

    const groupedByDateTransactions = groupBy(transactions, (transaction) =>
      toISODate(transaction.createdAt)
    );

    return resolveSequentially(
      Object.values(groupedByDateTransactions),
      async (groupedTransactions) => {
        const sourceTransaction = groupedTransactions[0];
        const practice = await Firestore.getDoc(sourceTransaction.practiceRef);
        const items = groupedTransactions.map((payment) => ({
          uid: payment.uid,
          type: InvoiceLineItemType.Deposit,
          description: payment.description ?? '',
          amount:
            payment.provider === TransactionProvider.Discount &&
            payment.type === TransactionType.Outgoing
              ? payment.amount * -1
              : payment.amount,
          tax: 0,
          taxStatus: TaxStrategy.GSTApplicable,
          quantity: 1,
        }));
        const invoice = Invoice.init({
          due: sourceTransaction.createdAt,
          practice: toNamedDocument(practice),
          from: toAccountDetails(practice),
          to: toAccountDetails(patient),
          reference: sourceTransaction.reference ?? '',
          issuedAt: sourceTransaction.createdAt,
          createdAt: sourceTransaction.createdAt,
          items,
        });

        return {
          invoice: {
            ...invoice,
            uid: sourceTransaction.uid,
          },
          transactions: groupedTransactions,
        };
      }
    );
  }

  async getTransactions(
    sourcePayments: IGetRecordResponse<ExactTransaction>[],
    migrationPractices: INamedDocument<IPractice>[],
    translationMap: TranslationMapHandler
  ): Promise<(IIdentifiable & ITransaction)[]> {
    return resolveSequentially(sourcePayments, async (sourcePayment) => {
      const payment = sourcePayment.data;
      const type = determineTransactionType(payment);
      const uid = compact([
        payment.id,
        payment.reference ?? payment.patient_id,
      ]).join('-');
      const provider = determineTransactionProvider(payment);
      const practice = await resolveExactStaffLocation(
        payment.user_code ?? '',
        translationMap,
        this.staffToPractice,
        migrationPractices
      );

      if (!practice) {
        throw new Error(
          `getTransactions - Failed to resolve practice for user: ${payment.user_code}`
        );
      }

      const createdAt = toTimestamp(
        toMomentTz(
          sourcePayment.record.translations.date,
          practice.settings.timezone
        )
      );
      const transaction = {
        reference: payment.reference ?? uid,
        type,
        status: TransactionStatus.Complete,
        from: '',
        to: '',
        amount: Math.abs(payment.amount),
        extendedData: payment,
        createdAt,
        practiceRef: practice.ref,
        description: payment.description,
      };

      return {
        ...Transaction.init({
          ...Transaction.internalReference(provider, uid),
          ...transaction,
        }),
      };
    });
  }

  buildNonTreatmentLineItems(
    charges: IGetRecordResponse<ExactTransaction>[]
  ): ICustomLineItem[] {
    return charges.map((transaction) => ({
      uid: compact([
        transaction.data.id,
        transaction.data.reference ?? transaction.data.patient_id,
      ]).join('-'),
      type: InvoiceLineItemType.Fee,
      description: transaction.data.description,
      amount: transaction.data.amount,
      tax: 0,
      taxStatus: TaxStrategy.GSTApplicable,
      quantity: 1,
    }));
  }
}

function determineTransactionProvider(
  transaction: IExactTransaction
): TransactionProvider {
  if (
    transaction.type === ExactTransactionType.Adjustment ||
    transaction.type === ExactTransactionType.Invoice ||
    transaction.type === ExactTransactionType.WriteOff
  ) {
    return TransactionProvider.Discount;
  }

  if (transaction.paymentType?.includes(TransactionProvider.Cash)) {
    return TransactionProvider.Cash;
  }

  return TransactionProvider.Manual;
}

export function getTransactionsForBalance(
  transactions: (IIdentifiable & ITransaction)[],
  balance: number
): [(IIdentifiable & ITransaction)[], (IIdentifiable & ITransaction)[]] {
  const result: (IIdentifiable & ITransaction)[] = [];
  const additionalTransactions: (IIdentifiable & ITransaction)[] = [];
  let remainingBalance = balance;
  let finalIndex = 0;

  if (balance <= 0) {
    return [result, transactions];
  }

  transactions.map((transaction, index) => {
    if (remainingBalance <= 0) {
      return;
    }

    const transactionAmount = getTransactionAmount(transaction);
    if (transactionAmount < remainingBalance) {
      result.push(transaction);
      remainingBalance -= transactionAmount;
      finalIndex = index;
      return;
    }
    if (transactionAmount > remainingBalance) {
      const balanceTransactions = splitTransaction(
        transaction,
        remainingBalance
      );
      result.push(...compact([first(balanceTransactions)]));
      finalIndex = index;
      additionalTransactions.push(...balanceTransactions.slice(1));
      remainingBalance = 0;
      return;
    }
    if (transactionAmount === remainingBalance) {
      result.push(transaction);
      remainingBalance = 0;
      finalIndex = index;
      return;
    }
  });
  transactions.splice(0, finalIndex + 1, ...additionalTransactions);
  return [result, transactions];
}

export function splitTransaction(
  transaction: IIdentifiable & ITransaction,
  requiredAmount: number
): (IIdentifiable & ITransaction)[] {
  const transactions: (IIdentifiable & ITransaction)[] = [];
  const transactionRemainder = transaction.amount - requiredAmount;

  if (transactionRemainder > 0) {
    transactions.push({
      ...transaction,
      amount: requiredAmount,
      reference: `${transaction.reference}-${transactions.length + 1}`,
      uid: `${transaction.uid}-${transactions.length + 1}`,
    });
    transactions.push({
      ...transaction,
      amount: transactionRemainder,
      reference: `${transaction.reference}-${transactions.length + 1}`,
      uid: `${transaction.uid}-${transactions.length + 1}`,
    });
    return transactions;
  }

  return [transaction];
}
